import React, { useEffect } from 'react';

import { Skeleton, Typography } from '@mui/material';
import { AlineButton, AlineTooltip, Spacer, TextTabSwitch } from 'src/components/generic';
import { useSelectCurrentUser } from 'src/gql';
import { Apollo } from 'src/gql/generated';
import { useDispatchNotification } from 'src/helpers';
import { makeStyles } from 'tss-react/mui';

import type { CheckoutState } from './Checkout';

const useStyles = makeStyles()(theme => ({
  checkoutButton: {
    marginTop: 'auto',
  },
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    width: '100%',
  },
  error: {
    color: theme.palette.error.main,
  },
  estimate: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    width: '100%',
  },
  estimateText: {
    whiteSpace: 'pre',
  },
  row: {
    alignItems: 'center',
    display: 'flex',
    height: 24,
  },
  tabButton: {
    padding: theme.spacing(0.5, 1),
  },
  tabSwitch: {
    alignSelf: 'center',
  },
  total: {
    borderTop: `1px solid ${theme.appColors.rule}`,
    fontWeight: 600,
    marginTop: theme.spacing(0.5),
    paddingTop: theme.spacing(1),
  },
}));

interface CheckoutEstimateProps {
  creatingCheckoutUrl: boolean;
  debouncedState: CheckoutState;
  handleCreateCheckoutUrl: () => Promise<void>;
  isDebouncing: boolean;
  setState: React.Dispatch<React.SetStateAction<CheckoutState>>;
  state: CheckoutState;
}

function EstimateContent({
  estimate,
  loading,
  state,
}: {
  estimate: Apollo.CheckoutEstimate | null;
  loading: boolean;
  state: CheckoutState;
}): JSX.Element {
  const { classes } = useStyles();

  return (
    <div className={classes.estimate}>
      <div className={classes.row}>
        <Typography
          className={classes.estimateText}
          variant="body2"
        >{`${state.numBasicSeatsPurchased} Basic `}</Typography>
        <EstimateDollarAmount
          dollars={estimate?.basicSeatUnitCost ?? 0}
          loading={loading}
          prefix="@ "
        />
        <Spacer />
        <EstimateDollarAmount dollars={estimate?.basicSeatTotal ?? 0} loading={loading} />
      </div>
      <div className={classes.row}>
        <Typography
          className={classes.estimateText}
          variant="body2"
        >{`${state.numFullSeatsPurchased} Full `}</Typography>
        <EstimateDollarAmount
          dollars={estimate?.fullSeatUnitCost ?? 0}
          loading={loading}
          prefix="@ "
        />
        <Spacer />
        <EstimateDollarAmount dollars={estimate?.fullSeatTotal ?? 0} loading={loading} />
      </div>
      <div className={`${classes.row} ${classes.total}`}>
        <Typography className={classes.estimateText} variant="subtitle1">
          Total
        </Typography>
        <Spacer />
        <EstimateDollarAmount dollars={estimate?.total ?? 0} loading={loading} />
      </div>
    </div>
  );
}

function EstimateDollarAmount({
  dollars,
  loading,
  prefix = '',
}: {
  dollars: number;
  loading: boolean;
  prefix?: string;
}): JSX.Element | null {
  const formatDollars = (dollars: number): string => {
    const formatter = new Intl.NumberFormat('en-US', {
      currency: 'USD',
      style: 'currency',
    });
    return formatter.format(dollars);
  };

  if (loading) return <Skeleton variant="text" width={56} />;

  return <Typography variant="body2">{`${prefix}${formatDollars(dollars)}`}</Typography>;
}

function BillingIntervalToggle({
  setState,
  state,
}: {
  setState: React.Dispatch<React.SetStateAction<CheckoutState>>;
  state: CheckoutState;
}): JSX.Element | null {
  const { classes } = useStyles();
  const currentUser = useSelectCurrentUser();
  const isSubscribed = Boolean(currentUser?.org.alineSubscription);
  const dispatchNotification = useDispatchNotification({
    message: 'The Enterprise plan is only available on an annual basis.',
    variant: 'info',
  });

  // The Enterprise plan is only available on an annual basis.
  useEffect(() => {
    if (state.selectedTier === Apollo.SubscriptionTier.Enterprise) {
      setState(prev => ({
        ...prev,
        interval: Apollo.SubscriptionInterval.Yearly,
      }));
    }
  }, [setState, state.selectedTier]);

  const handleMonthlyClick = () => {
    if (state.selectedTier === Apollo.SubscriptionTier.Enterprise) return dispatchNotification();
    setState(prev => ({ ...prev, interval: Apollo.SubscriptionInterval.Monthly }));
  };

  const handleYearlyClick = () => {
    setState(prev => ({ ...prev, interval: Apollo.SubscriptionInterval.Yearly }));
  };

  if (isSubscribed) return null;

  return (
    <TextTabSwitch
      className={classes.tabSwitch}
      leftButtonClassName={classes.tabButton}
      leftText="Billed Monthly"
      rightButtonClassName={classes.tabButton}
      rightText="Billed Yearly"
      selectedSide={state.interval === Apollo.SubscriptionInterval.Monthly ? 'left' : 'right'}
      onLeftClick={handleMonthlyClick}
      onRightClick={handleYearlyClick}
    />
  );
}

export function CheckoutEstimate({
  creatingCheckoutUrl,
  debouncedState,
  handleCreateCheckoutUrl,
  isDebouncing,
  setState,
  state,
}: CheckoutEstimateProps): JSX.Element {
  const { classes } = useStyles();
  const currentUser = useSelectCurrentUser();
  const isSwitchingPlans = Boolean(
    currentUser?.org.alineSubscription &&
    currentUser.org.alineSubscription.tier !== state.selectedTier
  );
  const { data, loading: isComputingEstimate } = Apollo.useComputeCheckoutEstimateQuery({
    skip: isDebouncing,
    variables: {
      input: {
        interval: debouncedState.interval,
        numNewBasicSeats: debouncedState.numBasicSeatsPurchased,
        numNewFullSeats: debouncedState.numFullSeatsPurchased,
        tier: debouncedState.selectedTier,
      },
    },
  });
  const isZeroSeats = state.numBasicSeatsPurchased === 0 && state.numFullSeatsPurchased === 0;
  const isCheckoutButtonDisabled =
    isSwitchingPlans || isDebouncing || isComputingEstimate || isZeroSeats;

  return (
    <div className={classes.container}>
      <BillingIntervalToggle setState={setState} state={state} />
      <EstimateContent
        estimate={data?.computeCheckoutEstimate ?? null}
        loading={isDebouncing || isComputingEstimate}
        state={state}
      />
      <AlineTooltip
        arrow
        placement="top"
        title={isSwitchingPlans ? 'Contact sales to switch plans.' : ''}
      >
        <div className={classes.checkoutButton}>
          <AlineButton
            fullWidth
            color="primary"
            disabled={isCheckoutButtonDisabled}
            loading={creatingCheckoutUrl}
            variant="contained"
            onClick={handleCreateCheckoutUrl}
          >
            Checkout
          </AlineButton>
        </div>
      </AlineTooltip>
    </div>
  );
}
